import { roundToOneDecimal } from '../domain/geometry.ts';

// #391 - one source of truth for the SVG <marker> arrowheads chemistry drawings need, so the
// live canvas (PaperVectorLayer) and the string export (svgExport) can't draw different heads.
// Each spec is a shape in its own small coordinate box with the arrow pointing +x; consumers
// reference it by id on a `marker-start` / `marker-end` / `marker-mid`, and `orient="auto"`
// rotates it to the path direction.
//
// Colour: the React <ArrowheadDefs> paints with `context-stroke` (the head takes the referencing
// line's stroke - so `var(--line-color)`, theme-correct, and a future coloured arrow just works);
// `arrowheadDefs()` for export bakes a literal colour in, since exported SVGs open in viewers
// that may not support context paints.
//
// Sizing: `markerUnits="userSpaceOnUse"` + a `scale` factor (driven off labelScale / export
// scale) so a head grows and shrinks with the drawing, and with viewport zoom via the parent
// viewBox - consistent with #385 / #387.

export type ArrowheadMarkerId =
  | 'arrowhead-full'
  | 'arrowhead-half'
  | 'arrowhead-open'
  | 'arrowhead-equilibrium-upper'
  | 'arrowhead-equilibrium-lower';

interface ArrowheadShape {
  readonly d: string;
  // filled -> a solid head (fill = paint, no stroke); otherwise an outline (stroke = paint).
  readonly filled: boolean;
}

export interface ArrowheadMarkerSpec {
  readonly id: ArrowheadMarkerId;
  // Nominal head size in px before `scale`; also the marker's viewBox.
  readonly length: number;
  readonly width: number;
  // Reference point (viewBox units) that lands on the path vertex the marker sits at. `full`
  // puts its *base* there (the shaft ends at the base, the triangle extends forward to the tip);
  // the outline heads put their *tip* there (the shaft runs to the tip, the barb sweeps back).
  readonly refX: number;
  readonly refY: number;
  readonly shapes: readonly ArrowheadShape[];
}

const HEAD_LENGTH = 9;
const HEAD_WIDTH = 8;
// Barb stroke weight in viewBox units; * scale on screen, a touch under the 3px bond stroke.
export const ARROWHEAD_BARB_WIDTH = 2.2;

const MID = HEAD_WIDTH / 2;

export const ARROWHEAD_MARKERS: readonly ArrowheadMarkerSpec[] = [
  {
    id: 'arrowhead-full',
    length: HEAD_LENGTH,
    width: HEAD_WIDTH,
    refX: 0,
    refY: MID,
    shapes: [{ d: `M0,0 L${HEAD_LENGTH},${MID} L0,${HEAD_WIDTH} Z`, filled: true }]
  },
  {
    id: 'arrowhead-half',
    length: HEAD_LENGTH,
    width: HEAD_WIDTH,
    refX: HEAD_LENGTH,
    refY: MID,
    shapes: [{ d: `M${HEAD_LENGTH},${MID} L0,0`, filled: false }]
  },
  {
    id: 'arrowhead-open',
    length: HEAD_LENGTH,
    width: HEAD_WIDTH,
    refX: HEAD_LENGTH,
    refY: MID,
    shapes: [{ d: `M0,0 L${HEAD_LENGTH},${MID} L0,${HEAD_WIDTH}`, filled: false }]
  },
  {
    id: 'arrowhead-equilibrium-upper',
    length: HEAD_LENGTH,
    width: HEAD_WIDTH,
    refX: HEAD_LENGTH,
    refY: MID,
    shapes: [{ d: `M${HEAD_LENGTH},${MID} L0,0`, filled: false }]
  },
  {
    id: 'arrowhead-equilibrium-lower',
    length: HEAD_LENGTH,
    width: HEAD_WIDTH,
    refX: HEAD_LENGTH,
    refY: MID,
    shapes: [{ d: `M${HEAD_LENGTH},${MID} L0,${HEAD_WIDTH}`, filled: false }]
  }
];

export function arrowheadMarkerSpec(id: ArrowheadMarkerId): ArrowheadMarkerSpec {
  const spec = ARROWHEAD_MARKERS.find((candidate) => candidate.id === id);
  if (!spec) throw new Error(`unknown arrowhead marker: ${id}`);
  return spec;
}

function shapeMarkup(shape: ArrowheadShape, paint: string): string {
  return shape.filled
    ? `<path d="${shape.d}" fill="${paint}" />`
    : `<path d="${shape.d}" fill="none" stroke="${paint}" stroke-width="${ARROWHEAD_BARB_WIDTH}" stroke-linecap="round" stroke-linejoin="round" />`;
}

function markerMarkup(spec: ArrowheadMarkerSpec, paint: string, scale: number, idPrefix: string): string {
  const attrs = [
    `id="${idPrefix}${spec.id}"`,
    `viewBox="0 0 ${spec.length} ${spec.width}"`,
    `refX="${spec.refX}"`,
    `refY="${spec.refY}"`,
    `markerWidth="${roundToOneDecimal(spec.length * scale)}"`,
    `markerHeight="${roundToOneDecimal(spec.width * scale)}"`,
    `markerUnits="userSpaceOnUse"`,
    `orient="auto"`
  ].join(' ');
  return `<marker ${attrs}>${spec.shapes.map((shape) => shapeMarkup(shape, paint)).join('')}</marker>`;
}

// The `<defs>` block for the export SVG. `paint` is a literal colour (context paints aren't
// portable); `scale` matches svgExport's labelScale so heads track the rest of the depiction.
// `idPrefix` (#429) namespaces the marker ids so an exported SVG inlined into the app DOM (a
// thumbnail) can't collide with the live canvas's `<defs>` or another export's.
export function arrowheadDefs(paint: string, scale = 1, idPrefix = ''): string {
  const markers = ARROWHEAD_MARKERS.map((spec) => `    ${markerMarkup(spec, paint, scale, idPrefix)}`).join('\n');
  return `<defs>\n${markers}\n  </defs>`;
}
